import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Fieldset,
  FileInput,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import {
  IconAddressBook,
  IconBoxSeam,
  IconUsers,
} from '@tabler/icons-react';
import { EntityPicker } from '../../shared/ui/EntityPicker';
import { LabelWithAction } from '../../shared/ui/LabelWithAction';
import { LabelWithHint } from '../../shared/ui/LabelWithHint';
import { CodeSelect } from '../../shared/ui/CodeSelect';
import { useNavigate, useParams } from 'react-router-dom';
import {
  buildDefaultRouteSegments,
  deriveOrderDirection,
  VAT_RATE_OPTIONS,
  type AddressBookEntryDto,
  type CargoTemplateDto,
  type CounterpartyCreateInput,
  type CounterpartyDto,
  type ExpeditorAgreementDto,
  type LinkCandidateDto,
  type ReferenceCodeDto,
  type OrderCreateInput,
  type OrderDetail,
  type OrderRouteSegmentInput,
  type RoutePointInput,
  type TransportMode,
  type ServiceCatalogItemDto,
} from '@expeditor/shared';
import {
  addressBookApi,
  cargoTemplatesApi,
  counterpartiesApi,
} from '../../shared/api/reference-books';
import { ordersApi, managerOrdersApi } from '../../shared/api/orders';
import { referenceDataApi } from '../../shared/api/reference-data';
import { managerClientsApi } from '../../shared/api/manager-clients';
import { expeditorProfileApi } from '../../shared/api/expeditor-profile';
import { apiClient } from '../../shared/api/client';
import { agreementsApi } from '../../shared/api/agreements';
import { serviceCatalogApi } from '../../shared/api/service-catalog';
import { applyCompanySuggestion, PartyRequisitesFields } from '../../shared/ui/PartyRequisitesFields';
import { RouteSegmentsEditor } from './RouteSegmentsEditor';
import { CompanyLookup } from '../../shared/ui/CompanyLookup';
import { DateField } from '../../shared/ui/DateField';
import {
  PositionsEditor,
  CargoGroupEditor,
  emptyCargoGroup,
  makeUnit,
  shipmentTypeFromUnits,
  unitsWord,
  type CargoGroupForm,
  type TransportUnitForm,
} from '../../shared/ui/transport-units';
import { directionLabel } from '../../shared/ui/order-labels';
import { useAuthStore } from '../../shared/auth/auth.store';

/**
 * Форма создания/правки поручения.
 *
 * - При создании id=undefined. После успешного create — редирект на карточку.
 * - В режиме правки (?:id) подгружаем поручение и заполняем поля.
 *   Правка возможна только в DRAFT — если статус другой, отображаем баннер.
 *
 * Порядок и группировка секций повторяют печатный бланк «Поручение экспедитору»
 * (Приказ Минтранса № 23, Приложение № 1), чтобы пользователю было привычно:
 * Стороны → Перевозка → Груз → Стоимость → Страхование и услуги →
 * Требуемые документы → Особые отметки. См. docs/compliance-poruchenie-277.md.
 *
 * Комбо-поля «Откуда / Куда / Груз / Контрагент»: значение можно выбрать из
 * справочника (Select) или ввести вручную (TextInput ниже). После сохранения
 * нового адреса/груза/контрагента предлагается сохранить запись в справочник.
 */
/** Минимальная форма ответа GET /clients/me — нужны только реквизиты для шапки. */
interface ClientMeReqs {
  client?: {
    legalRu?: { inn?: string; kpp?: string } | null;
    entrepreneur?: { inn?: string } | null;
    legalForeign?: { taxId?: string } | null;
  } | null;
}

/** Если задан список кандидатов — фильтруем его по запросу; иначе — полный поиск. */
function filterOrSearch(
  constraint: ReferenceCodeDto[] | null,
  fullSearch: () => Promise<ReferenceCodeDto[]>,
  q: string,
): Promise<ReferenceCodeDto[]> {
  if (constraint && constraint.length) {
    const term = q.trim().toLowerCase();
    const rows = term
      ? constraint.filter(
          (x) => x.code.toLowerCase().startsWith(term) || x.name.toLowerCase().includes(term),
        )
      : constraint;
    return Promise.resolve(rows);
  }
  return fullSearch();
}

/** Код вида транспорта ФТС → внутренний enum TransportMode (для ФНС/печати/котировок). */
const FTS_TO_MODE: Record<string, string> = {
  '10': 'SEA',
  '20': 'RAIL',
  '30': 'AUTO',
  '31': 'AUTO',
  '32': 'AUTO',
  '40': 'AIR',
  '50': 'ANY',
  '71': 'ANY',
  '72': 'ANY',
  '80': 'INLAND_WATER',
  '90': 'AUTO',
  '99': 'ANY',
};
/** enum TransportMode → представительный код ФТС (для показа старых поручений). */
const MODE_TO_FTS: Record<string, string> = {
  SEA: '10',
  RAIL: '20',
  AUTO: '30',
  AIR: '40',
  INLAND_WATER: '80',
};
function deriveTransportModes(ftsCodes: string[]): string[] {
  const modes = new Set<string>();
  for (const c of ftsCodes) modes.add(FTS_TO_MODE[c] ?? 'ANY');
  return [...modes];
}
function transportModesToFts(modes: string[]): string[] {
  const out: string[] = [];
  for (const m of modes) {
    const c = MODE_TO_FTS[m];
    if (c && !out.includes(c)) out.push(c);
  }
  return out;
}

export function OrderFormPage({ mode = 'client' }: { mode?: 'client' | 'manager' }) {
  const { id, clientId: routeClientId } = useParams<{ id?: string; clientId?: string }>();
  const isManager = mode === 'manager';
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [managerClientName, setManagerClientName] = useState<string | null>(null);

  const [addresses, setAddresses] = useState<AddressBookEntryDto[]>([]);
  const [cargos, setCargos] = useState<CargoTemplateDto[]>([]);
  const [counterparties, setCounterparties] = useState<CounterpartyDto[]>([]);
  // Стороны «Клиент» и «Экспедитор» — read-only, из базы (2c).
  const clientDisplayName = useAuthStore((s) => s.user?.displayName ?? null);
  const [clientInn, setClientInn] = useState<string | null>(null);
  const [clientKpp, setClientKpp] = useState<string | null>(null);
  const [expeditorName, setExpeditorName] = useState<string | null>(null);
  const [expeditorInn, setExpeditorInn] = useState<string | null>(null);
  const [expeditorKpp, setExpeditorKpp] = useState<string | null>(null);
  // Договор ТЭО — основание поручения (обязателен для подачи/документов).
  const [agreements, setAgreements] = useState<ExpeditorAgreementDto[]>([]);
  const [agreementId, setAgreementId] = useState<string>('');
  // «Проверка»: ошибки по полям (подсветка + текст под полем) + флаг «проверяли».
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [checkOk, setCheckOk] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Перечитывание поручения после сохранения (форма остаётся открытой).
  const [reloadKey, setReloadKey] = useState(0);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  // Спросить про несохранённые изменения при закрытии.
  const [closeAsk, setCloseAsk] = useState(false);
  /** Слепок DTO на момент последней загрузки/сохранения — база для isDirty. */
  const savedSnapshot = useRef('');
  const [error, setError] = useState<string | null>(null);

  // form state
  // Виды транспорта (мультимодальная — несколько).
  // Виды транспорта — коды классификатора ФТС (множественный выбор); внутренний
  // enum transportModes выводим маппингом при отправке.
  const [transportTypeCodes, setTransportTypeCodes] = useState<string[]>([]);
  const [transportOptions, setTransportOptions] = useState<{ value: string; label: string }[]>([]);
  // Уточнение маршрута по плечам (опц.; актуально для мультимодальной перевозки).
  const [routeDetailed, setRouteDetailed] = useState(false);
  const [routeSegments, setRouteSegments] = useState<OrderRouteSegmentInput[]>([]);

  const [cargoTemplateId, setCargoTemplateId] = useState<string | null>(null);
  // Единичные значения из выбранного шаблона — нужны для auto-fill
  // при изменении количества (qty × unit value).
  const [unitWeightKg, setUnitWeightKg] = useState<number | null>(null);
  const [unitVolumeM3, setUnitVolumeM3] = useState<number | null>(null);
  const [cargoTemplatePickerOpen, setCargoTemplatePickerOpen] = useState(false);
  // Пикеры контрагентов / адресов: для какой стороны открыт.
  // Плательщик: по умолчанию сам клиент; иначе — контрагент (снимок реквизитов).
  const [payerIsClient, setPayerIsClient] = useState(true);
  const [payerCounterpartyId, setPayerCounterpartyId] = useState<string | null>(null);
  const [payerName, setPayerName] = useState('');
  const [payerType, setPayerType] = useState('');
  const [payerInn, setPayerInn] = useState('');
  const [payerKpp, setPayerKpp] = useState('');
  const [payerOgrn, setPayerOgrn] = useState('');
  const [payerAddress, setPayerAddress] = useState('');
  const [payerPickerOpen, setPayerPickerOpen] = useState(false);
  const [counterpartyPicker, setCounterpartyPicker] = useState<null | 'pickup' | 'dropoff'>(
    null,
  );
  const [addressPicker, setAddressPicker] = useState<null | 'pickup' | 'dropoff'>(null);
  const [cargoName, setCargoName] = useState('');
  // Категория груза скрыта в форме — поле сохраняется для совместимости.
  const [cargoCategory, setCargoCategory] = useState('');
  const [cargoUnits, setCargoUnits] = useState<number | string>('');
  const [cargoWeightKg, setCargoWeightKg] = useState<number | string>('');
  const [cargoWeightNetKg, setCargoWeightNetKg] = useState<number | string>('');
  const [cargoVolumeM3, setCargoVolumeM3] = useState<number | string>('');
  // Стоимость: пользователь вводит "без налога" и ставку, итог считается.
  const [cargoValueNet, setCargoValueNet] = useState<number | string>('');
  const [cargoVatRate, setCargoVatRate] = useState<string>(''); // '0' | '10' | '20' | ''
  const [cargoValueCurrency, setCargoValueCurrency] = useState<string>('RUB');
  const [cargoIsDangerous, setCargoIsDangerous] = useState(false);
  const [cargoAdrClass, setCargoAdrClass] = useState('');
  const [cargoUnNumber, setCargoUnNumber] = useState('');
  const [cargoTnVed, setCargoTnVed] = useState('');
  const [cargoEtSng, setCargoEtSng] = useState('');
  const [cargoGng, setCargoGng] = useState('');
  const [cargoPackaging, setCargoPackaging] = useState('');
  const [cargoPackagingCode, setCargoPackagingCode] = useState(''); // код упаковки (ФТС)
  const [cargoOriginCountryCode, setCargoOriginCountryCode] = useState(''); // код страны (ФТС)
  const [saveCargoToBook, setSaveCargoToBook] = useState(false);

  // Признаки груза по коду ЕТСНГ (опасный/скоропортящийся) — для подсказки и автоотметки.
  const [etsngInfo, setEtsngInfo] = useState<{ dangerous: boolean; perishable: boolean } | null>(
    null,
  );

  // Применяет признаки из строки ЕТСНГ: опасность автоматически ставит отметку
  // «Опасный груз» (снимать вручную — не трогаем, чтобы не спорить с пользователем).
  const applyEtsngInfo = (dto: ReferenceCodeDto | null) => {
    if (!dto) {
      setEtsngInfo(null);
      return;
    }
    setEtsngInfo({ dangerous: !!dto.dangerous, perishable: !!dto.perishable });
    if (dto.dangerous) setCargoIsDangerous(true);
  };

  // Переходник ГНГ↔ЕТСНГ: ограничение вариантов встречного поля.
  // null — выбор из всего справочника; массив — только эти кандидаты (в самом поле).
  const [etsngConstraint, setEtsngConstraint] = useState<ReferenceCodeDto[] | null>(null);
  const [gngConstraint, setGngConstraint] = useState<ReferenceCodeDto[] | null>(null);

  const applyEtsngByCode = (code: string) => {
    referenceDataApi
      .byCode('etsng', code)
      .then(applyEtsngInfo)
      .catch(() => {});
  };

  // По выбранному коду в одном поле настраивает встречное поле:
  //  • встречное уже заполнено — ничего не делаем;
  //  • одно соответствие — подставляем;
  //  • несколько — ограничиваем список этими кандидатами (выбор прямо в поле);
  //  • нет соответствий — оставляем выбор из всего справочника (constraint = null).
  const resolveCounterpart = async (from: 'gng' | 'etsng', code: string) => {
    const c = code.trim();
    if (!c) return;
    const targetIsEtsng = from === 'gng';
    const targetFilled = (targetIsEtsng ? cargoEtSng : cargoGng).trim();
    if (targetFilled) return;
    let list: LinkCandidateDto[] = [];
    try {
      list = await referenceDataApi.link(from, c);
    } catch {
      return;
    }
    const prod = list.filter((p) => p.production);
    const use = prod.length ? prod : list;
    const setConstraint = targetIsEtsng ? setEtsngConstraint : setGngConstraint;
    if (use.length === 1) {
      const only = use[0];
      if (targetIsEtsng) {
        setCargoEtSng(only.code);
        applyEtsngByCode(only.code);
      } else {
        setCargoGng(only.code);
      }
      setConstraint(null);
    } else if (use.length >= 2) {
      setConstraint(use.map((x) => ({ code: x.code, name: x.name, detail: x.detail })));
    } else {
      setConstraint(null);
    }
  };

  // Справочник видов транспорта (ФТС) для множественного выбора.
  useEffect(() => {
    referenceDataApi
      .classifierSearch('transport', '', 100)
      .then((rows) =>
        setTransportOptions(rows.map((r) => ({ value: r.code, label: `${r.code} — ${r.name}` }))),
      )
      .catch(() => {});
  }, []);

  // Реквизиты формата ФНС ЕД-1-26/277@ (бланк Минтранс № 23). См. compliance-poruchenie-277.md.
  const [cargoOriginCountry, setCargoOriginCountry] = useState(''); // страна происхождения груза
  const [cargoOkpd2, setCargoOkpd2] = useState(''); // товарный код ОКПД 2
  const [cargoMarking, setCargoMarking] = useState(''); // маркировка
  const [cargoTareWeightKg, setCargoTareWeightKg] = useState<number | string>(''); // вес упаковки (тары)
  // Вес упаковки заполняется автоматически как (брутто − нетто), пока его не
  // изменили вручную; после ручной правки авторасчёт отключается.
  const [tareEdited, setTareEdited] = useState(false);

  // Автосвязь ГНГ↔ЕТСНГ по изменению кода (надёжнее, чем через onOptionPick).
  useEffect(() => {
    if (cargoGng.trim()) resolveCounterpart('gng', cargoGng.trim());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoGng]);
  useEffect(() => {
    const code = cargoEtSng.trim();
    if (!code) return;
    resolveCounterpart('etsng', code);
    applyEtsngByCode(code); // признак опасности
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargoEtSng]);
  // Наименования по кодам классификаторов (страна/упаковка) — для сохранения.
  useEffect(() => {
    const code = cargoOriginCountryCode.trim();
    if (!code) return;
    referenceDataApi
      .classifierByCode('country', code)
      .then((r) => {
        if (r) setCargoOriginCountry(r.name);
      })
      .catch(() => {});
  }, [cargoOriginCountryCode]);
  useEffect(() => {
    const code = cargoPackagingCode.trim();
    if (!code) return;
    referenceDataApi
      .classifierByCode('packaging', code)
      .then((r) => {
        if (r) setCargoPackaging(r.name);
      })
      .catch(() => {});
  }, [cargoPackagingCode]);

  // Вес упаковки = брутто − нетто (если оба заданы и разница неотрицательна).
  const computeTare = (gross: number | string, net: number | string): number | string => {
    if (gross === '' || net === '') return '';
    const t = Number(gross) - Number(net);
    if (Number.isNaN(t) || t < 0) return '';
    return Math.round(t * 100) / 100;
  };
  const handleGrossChange = (v: number | string) => {
    setCargoWeightKg(v);
    if (!tareEdited) setCargoTareWeightKg(computeTare(v, cargoWeightNetKg));
  };
  const handleNetChange = (v: number | string) => {
    setCargoWeightNetKg(v);
    if (!tareEdited) setCargoTareWeightKg(computeTare(cargoWeightKg, v));
  };
  const handleTareChange = (v: number | string) => {
    setCargoTareWeightKg(v);
    setTareEdited(true);
  };
  const [packageLengthM, setPackageLengthM] = useState<number | string>(''); // размер упаковки, Д
  const [packageWidthM, setPackageWidthM] = useState<number | string>(''); // размер упаковки, Ш
  const [packageHeightM, setPackageHeightM] = useState<number | string>(''); // размер упаковки, В
  const [notifyParties, setNotifyParties] = useState<string[]>([]); // кого уведомить о прибытии
  const [expeditorTakesPossession, setExpeditorTakesPossession] = useState(false); // приём во владение
  const [specialMarks, setSpecialMarks] = useState(''); // особые отметки
  const [tempMinC, setTempMinC] = useState<number | string>(''); // климат: температура от
  const [tempMaxC, setTempMaxC] = useState<number | string>(''); // климат: температура до

  /** Рассчитанные стоимости НДС и итога — для отображения. */
  const computedVatAmount = (() => {
    const n = Number(cargoValueNet);
    // «Без НДС» (-1) трактуем как 0 при расчёте суммы НДС.
    const r = Math.max(0, Number(cargoVatRate));
    if (!Number.isFinite(n) || !Number.isFinite(r) || cargoValueNet === '' || cargoVatRate === '')
      return null;
    return Math.round(((n * r) / 100) * 100) / 100;
  })();
  const computedValueGross = (() => {
    const n = Number(cargoValueNet);
    const r = Math.max(0, Number(cargoVatRate || '0'));
    if (!Number.isFinite(n) || cargoValueNet === '') return null;
    return Math.round(n * (1 + r / 100) * 100) / 100;
  })();

  // Условия поставки + поля для CIF.
  const [deliveryTerms, setDeliveryTerms] = useState<string>('');
  const [containerNumber, setContainerNumber] = useState('');
  const [billOfLadingNumber, setBillOfLadingNumber] = useState('');

  // Контейнеры как единицы транспорта (если shipmentType=Контейнер).
  const [units, setUnits] = useState<TransportUnitForm[]>([]);
  // Один груз во всех контейнерах (по умолчанию) или разный груз по группам.
  const [sameCargoForAll, setSameCargoForAll] = useState(true);
  const [cargoGroups, setCargoGroups] = useState<CargoGroupForm[]>([]);
  // Виды транспорта поручения (enum) — выводятся из выбранных кодов ФТС.
  // Нужны для раскладки маршрута по плечам.
  const selectedModes = deriveTransportModes(transportTypeCodes) as TransportMode[];
  // Тип отправки больше не выбирается — он складывается из добавленных единиц
  // («Контейнер», «Вагон» или оба сразу).
  const shipmentType = shipmentTypeFromUnits(units);
  // Слова для подписей: «в контейнерах» / «в вагонах» / «в единицах транспорта».
  const unitWordPlural = unitsWord(units).inMany;
  const showCargoGroups = units.length > 1;
  const toggleSameCargo = (checked: boolean) => {
    setSameCargoForAll(checked);
    // При выключении — по группе на каждую единицу (предзаполнение).
    if (!checked && cargoGroups.length === 0) {
      setCargoGroups(
        units.map((u) => ({
          ...emptyCargoGroup(),
          unitIds: [u.id],
        })),
      );
    }
  };

  // Желаемый период перевозки.
  const [preferredDateFrom, setPreferredDateFrom] = useState('');
  const [preferredDateTo, setPreferredDateTo] = useState('');

  // МСДС-файлы для опасного груза.
  const [pendingMsdsRu, setPendingMsdsRu] = useState<File[]>([]);
  const [pendingMsdsEn, setPendingMsdsEn] = useState<File[]>([]);

  const [pickupAddressId, setPickupAddressId] = useState<string | null>(null);
  const [pickupCountry, setPickupCountry] = useState('');
  const [pickupAddress, setPickupAddress] = useState('');
  const [pickupContact, setPickupContact] = useState('');
  const [pickupPhone, setPickupPhone] = useState('');
  const [pickupCounterpartyId, setPickupCounterpartyId] = useState<string | null>(null);
  const [pickupCounterpartyName, setPickupCounterpartyName] = useState('');
  // Снимок реквизитов грузоотправителя (2b).
  const [pickupCpType, setPickupCpType] = useState('');
  const [pickupCpInn, setPickupCpInn] = useState('');
  const [pickupCpKpp, setPickupCpKpp] = useState('');
  const [pickupCpOgrn, setPickupCpOgrn] = useState('');
  const [savePickupAddressToBook, setSavePickupAddressToBook] = useState(false);
  const [savePickupCounterpartyToBook, setSavePickupCounterpartyToBook] = useState(false);

  const [dropoffAddressId, setDropoffAddressId] = useState<string | null>(null);
  const [dropoffCountry, setDropoffCountry] = useState('');
  // Направление перевозки выводится из стран отправления/назначения и только
  // показывается — вручную его больше не выбирают.
  const derivedDirection = deriveOrderDirection(pickupCountry, dropoffCountry);
  const [dropoffAddress, setDropoffAddress] = useState('');
  const [dropoffContact, setDropoffContact] = useState('');
  const [dropoffPhone, setDropoffPhone] = useState('');
  const [dropoffCounterpartyId, setDropoffCounterpartyId] = useState<string | null>(null);
  const [dropoffCounterpartyName, setDropoffCounterpartyName] = useState('');
  // Снимок реквизитов грузополучателя (2b).
  const [dropoffCpType, setDropoffCpType] = useState('');
  const [dropoffCpInn, setDropoffCpInn] = useState('');
  const [dropoffCpKpp, setDropoffCpKpp] = useState('');
  const [dropoffCpOgrn, setDropoffCpOgrn] = useState('');
  const [saveDropoffAddressToBook, setSaveDropoffAddressToBook] = useState(false);
  const [saveDropoffCounterpartyToBook, setSaveDropoffCounterpartyToBook] = useState(false);

  const [extraServices, setExtraServices] = useState<string[]>([]);
  // Справочник доп. услуг (активные, из админ-справочника).
  const [serviceCatalog, setServiceCatalog] = useState<ServiceCatalogItemDto[]>([]);
  const [comment, setComment] = useState('');

  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<OrderDetail['attachments']>([]);

  useEffect(() => {
    void (async () => {
      try {
        // Реквизиты экспедитора и справочник услуг — нужны в обоих режимах.
        const [exp, svc] = await Promise.all([
          expeditorProfileApi.get().catch(() => null),
          serviceCatalogApi.listActive().catch(() => []),
        ]);
        setServiceCatalog(svc);
        if (exp) {
          setExpeditorName(exp.shortName ?? exp.fullName ?? null);
          setExpeditorInn(exp.inn ?? null);
          setExpeditorKpp(exp.kpp ?? null);
        }

        if (isManager) {
          // Личные справочники клиента менеджеру недоступны — быстрозаполнения скрыты.
          setAddresses([]);
          setCargos([]);
          setCounterparties([]);
        } else {
          const [a, c, cp, me, ags] = await Promise.all([
            addressBookApi.list(),
            cargoTemplatesApi.list(),
            counterpartiesApi.list(),
            apiClient
              .get<ClientMeReqs>('/clients/me')
              .then((r) => r.data)
              .catch(() => null),
            agreementsApi.listActive().catch(() => []),
          ]);
          setAddresses(a);
          setCargos(c);
          setCounterparties(cp);
          setAgreements(ags);
          // Если действующий договор один — подставляем автоматически (при создании).
          if (!isEdit && ags.length === 1) setAgreementId(ags[0].id);
          // Реквизиты клиента из его подтипа (2c).
          const cl = me?.client;
          if (cl) {
            setClientInn(cl.legalRu?.inn ?? cl.entrepreneur?.inn ?? cl.legalForeign?.taxId ?? null);
            setClientKpp(cl.legalRu?.kpp ?? null);
          }
        }

        let loadedClientId: string | null = null;
        if (isEdit && id) {
          const order = isManager ? await managerOrdersApi.getOne(id) : await ordersApi.getOne(id);
          loadedClientId = order.clientId;
          if (order.status !== 'DRAFT') {
            setError(`Поручение в статусе ${order.status} — правка недоступна`);
          }
          setTransportTypeCodes(
            order.transportTypeCodes?.length
              ? order.transportTypeCodes
              : transportModesToFts(
                  order.transportModes?.length ? order.transportModes : [order.transportMode],
                ),
          );
          setAgreementId(order.agreementId ?? '');
          setCargoName(order.cargoName);
          setCargoCategory(order.cargoCategory ?? '');
          setCargoUnits(order.cargoUnits ?? '');
          setCargoWeightKg(order.cargoWeightKg);
          setCargoWeightNetKg(order.cargoWeightNetKg ?? '');
          setCargoVolumeM3(order.cargoVolumeM3 ?? '');
          setCargoValueNet(order.cargoValueNet ?? '');
          setCargoVatRate(order.cargoVatRate !== null ? String(order.cargoVatRate) : '');
          setCargoValueCurrency(order.cargoValueCurrency ?? 'RUB');
          setCargoIsDangerous(order.cargoIsDangerous);
          setCargoAdrClass(order.cargoAdrClass ?? '');
          setCargoUnNumber(order.cargoUnNumber ?? '');
          setCargoTnVed(order.cargoTnVed ?? '');
          setCargoEtSng(order.cargoEtSng ?? '');
          setCargoGng(order.cargoGng ?? '');
          setCargoPackaging(order.cargoPackaging ?? '');
          setCargoPackagingCode(order.cargoPackagingCode ?? '');
          setCargoOriginCountry(order.cargoOriginCountry ?? '');
          setCargoOriginCountryCode(order.cargoOriginCountryCode ?? '');
          setCargoOkpd2(order.cargoOkpd2 ?? '');
          setCargoMarking(order.cargoMarking ?? '');
          setCargoTareWeightKg(order.cargoTareWeightKg ?? '');
          // У сохранённого поручения вес упаковки считаем заданным — не перетираем.
          setTareEdited(order.cargoTareWeightKg != null);
          setPackageLengthM(order.packageLengthM ?? '');
          setPackageWidthM(order.packageWidthM ?? '');
          setPackageHeightM(order.packageHeightM ?? '');
          setNotifyParties(order.notifyParties ?? []);
          setExpeditorTakesPossession(order.expeditorTakesPossession ?? false);
          setSpecialMarks(order.specialMarks ?? '');
          setTempMinC(order.tempMinC ?? '');
          setTempMaxC(order.tempMaxC ?? '');
          // Единицы транспорта: из transportUnits; для старых поручений —
          // fallback из legacy containers (по одному на элемент).
          const loadedUnits: TransportUnitForm[] =
            order.transportUnits && order.transportUnits.length > 0
              ? order.transportUnits.map((u) => ({
                  id: u.id ?? makeUnit().id,
                  kind: (u.kind ?? 'CONTAINER') as TransportUnitForm['kind'],
                  type: u.type,
                  number: u.number ?? '',
                  ownership: (u.ownership ?? '') as TransportUnitForm['ownership'],
                }))
              : ((order.containers as Array<{ type: string; number?: string }>) ?? []).map(
                  (c) => ({ ...makeUnit('CONTAINER', c.type), number: c.number ?? '' }),
                );
          setUnits(loadedUnits);
          // Карта: legacy-ключ (номер или ярлык) → id единицы — чтобы перенести
          // старое распределение cargoGroups.containers на unitIds.
          const legacyKeyToId: Record<string, string> = {};
          loadedUnits.forEach((u, i) => {
            const legacyKey = u.number || `Контейнер ${i + 1} (${u.type})`;
            legacyKeyToId[legacyKey] = u.id;
            if (u.number) legacyKeyToId[u.number] = u.id;
          });
          setSameCargoForAll(order.sameCargoForAll ?? true);
          setCargoGroups(
            (order.cargoGroups ?? []).map((g) => ({
              unitIds:
                g.unitIds && g.unitIds.length > 0
                  ? g.unitIds
                  : (g.containers ?? [])
                      .map((k) => legacyKeyToId[k])
                      .filter((x): x is string => Boolean(x)),
              cargoName: g.cargoName ?? '',
              cargoWeightKg: g.cargoWeightKg ?? '',
              cargoWeightNetKg: g.cargoWeightNetKg ?? '',
              cargoVolumeM3: g.cargoVolumeM3 ?? '',
              cargoUnits: g.cargoUnits ?? '',
              cargoPackaging: g.cargoPackaging ?? '',
              cargoTnVed: g.cargoTnVed ?? '',
              cargoMarking: g.cargoMarking ?? '',
              cargoIsDangerous: g.cargoIsDangerous ?? false,
              cargoAdrClass: g.cargoAdrClass ?? '',
            })),
          );
          // preferredDateFrom/To — основные. Если в новом поручении не заданы,
          // fallback на даты у pickup/dropoff (для старых поручений).
          const pickupDate = order.routePoints.find((p) => p.kind === 'PICKUP')?.date ?? null;
          const dropoffDate = order.routePoints.find((p) => p.kind === 'DROPOFF')?.date ?? null;
          setPreferredDateFrom(
            order.preferredDateFrom?.slice(0, 10) ?? pickupDate?.slice(0, 10) ?? '',
          );
          setPreferredDateTo(
            order.preferredDateTo?.slice(0, 10) ?? dropoffDate?.slice(0, 10) ?? '',
          );
          setDeliveryTerms(order.deliveryTerms ?? '');
          setContainerNumber(order.containerNumber ?? '');
          setBillOfLadingNumber(order.billOfLadingNumber ?? '');
          setComment(order.comment ?? '');
          setExtraServices(order.extraServices.map((s) => s.code));
          setExistingAttachments(order.attachments);
          setPayerIsClient(order.payerIsClient ?? true);
          setPayerName(order.payerName ?? '');
          setPayerType(order.payerType ?? '');
          setPayerInn(order.payerInn ?? '');
          setPayerKpp(order.payerKpp ?? '');
          setPayerOgrn(order.payerOgrn ?? '');
          setPayerAddress(order.payerAddress ?? '');
          setRouteDetailed(order.routeDetailed);
          setRouteSegments(
            order.routeSegments.map((s) => ({
              ...s,
              dateFrom: s.dateFrom ?? undefined,
              dateTo: s.dateTo ?? undefined,
            })),
          );

          const pickup = order.routePoints.find((p) => p.kind === 'PICKUP');
          const dropoff = order.routePoints.find((p) => p.kind === 'DROPOFF');
          if (pickup) {
            setPickupCountry(pickup.country ?? '');
            setPickupAddress(pickup.address);
            setPickupContact(pickup.contactName ?? '');
            setPickupPhone(pickup.contactPhone ?? '');
            setPickupCounterpartyName(pickup.counterpartyName ?? '');
            setPickupCpType(pickup.counterpartyType ?? '');
            setPickupCpInn(pickup.counterpartyInn ?? '');
            setPickupCpKpp(pickup.counterpartyKpp ?? '');
            setPickupCpOgrn(pickup.counterpartyOgrn ?? '');
          }
          if (dropoff) {
            setDropoffCountry(dropoff.country ?? '');
            setDropoffAddress(dropoff.address);
            setDropoffContact(dropoff.contactName ?? '');
            setDropoffPhone(dropoff.contactPhone ?? '');
            setDropoffCounterpartyName(dropoff.counterpartyName ?? '');
            setDropoffCpType(dropoff.counterpartyType ?? '');
            setDropoffCpInn(dropoff.counterpartyInn ?? '');
            setDropoffCpKpp(dropoff.counterpartyKpp ?? '');
            setDropoffCpOgrn(dropoff.counterpartyOgrn ?? '');
          }
        }

        // Режим менеджера: имя клиента + его действующие договоры ТЭО.
        if (isManager) {
          const targetClientId = isEdit ? loadedClientId : routeClientId ?? null;
          if (targetClientId) {
            const [detail, ags] = await Promise.all([
              managerClientsApi.getOne(targetClientId).catch(() => null),
              agreementsApi.listForClient(targetClientId).catch(() => []),
            ]);
            if (detail) setManagerClientName(detail.displayName);
            const activeAgs = ags.filter((a) => !a.archivedAt);
            setAgreements(activeAgs);
            if (!isEdit && activeAgs.length === 1) setAgreementId(activeAgs[0].id);
          }
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить данные');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, reloadKey]);

  // Слепок «как загрузилось» — по нему определяем несохранённые изменения.
  // Ставится после того, как загрузка разложила данные по состоянию формы.
  useEffect(() => {
    if (!loading) savedSnapshot.current = JSON.stringify(buildDto());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, reloadKey]);

  /**
   * Применение шаблона груза к полям формы.
   *
   * Единичные вес и объём из шаблона СОХРАНЯЮТСЯ отдельно (unitWeightKg /
   * unitVolumeM3) — на их основе при изменении «Количество» автоматически
   * перерасчитываются Вес и Объём поручения.
   */
  const applyCargoTemplate = (tplId: string | null) => {
    setCargoTemplateId(tplId);
    if (!tplId) {
      setUnitWeightKg(null);
      setUnitVolumeM3(null);
      return;
    }
    const tpl = cargos.find((c) => c.id === tplId);
    if (!tpl) return;
    setCargoName(tpl.name);
    setCargoCategory(tpl.category ?? '');
    setCargoPackaging(tpl.packaging ?? '');
    setUnitWeightKg(tpl.defaultWeightKg);
    setUnitVolumeM3(tpl.defaultVolumeM3);
    setCargoTnVed(tpl.tnVedCode ?? '');
    setCargoGng(tpl.gngCode ?? '');
    setCargoEtSng(tpl.etSngCode ?? '');
    setCargoIsDangerous(tpl.isDangerous);
    setCargoAdrClass(tpl.adrClass ?? '');
    // Если уже есть количество — сразу пересчитаем.
    if (cargoUnits !== '' && Number(cargoUnits) > 0) {
      const qty = Number(cargoUnits);
      if (tpl.defaultWeightKg !== null) {
        setCargoWeightKg(round2(qty * tpl.defaultWeightKg));
        setCargoWeightNetKg(round2(qty * tpl.defaultWeightKg));
      }
      if (tpl.defaultVolumeM3 !== null) setCargoVolumeM3(round2(qty * tpl.defaultVolumeM3));
    }
  };

  /** Изменилось количество — пересчитываем вес/объём из единичных значений шаблона. */
  const onUnitsChange = (v: number | string) => {
    setCargoUnits(v);
    const qty = Number(v);
    if (!Number.isFinite(qty) || qty <= 0) return;
    if (unitWeightKg !== null && unitWeightKg > 0) {
      setCargoWeightKg(round2(qty * unitWeightKg));
      setCargoWeightNetKg(round2(qty * unitWeightKg));
    }
    if (unitVolumeM3 !== null && unitVolumeM3 > 0) {
      setCargoVolumeM3(round2(qty * unitVolumeM3));
    }
  };

  const round2 = (n: number) => Math.round(n * 100) / 100;

  const applyAddress = (
    setId: (v: string | null) => void,
    setCountry: (v: string) => void,
    setAddr: (v: string) => void,
    setContact: (v: string) => void,
    setPhone: (v: string) => void,
    tplId: string | null,
  ) => {
    setId(tplId);
    if (!tplId) return;
    const tpl = addresses.find((a) => a.id === tplId);
    if (!tpl) return;
    setCountry(tpl.country ?? '');
    setAddr(tpl.address);
    setContact(tpl.contactName ?? '');
    setPhone(tpl.contactPhone ?? '');
  };

  /**
   * Применить контрагента к маршруту. Помимо имени тянет также адрес и
   * контакты — пользователь сможет их подправить (поля редактируемые).
   * `force` — затирать ли уже заполненные поля (обычно `true` при ручном
   * выборе из дропдауна, чтобы переключение контрагента переписывало).
   */
  const applyCounterparty = (target: 'pickup' | 'dropoff', tplId: string | null) => {
    const tpl = tplId ? counterparties.find((c) => c.id === tplId) : null;
    if (target === 'pickup') {
      setPickupCounterpartyId(tplId);
      if (!tpl) return;
      setPickupCounterpartyName(tpl.name);
      if (tpl.country) setPickupCountry(tpl.country);
      if (tpl.address) setPickupAddress(tpl.address);
      if (tpl.contactName) setPickupContact(tpl.contactName);
      if (tpl.contactPhone) setPickupPhone(tpl.contactPhone);
      setPickupCpType(tpl.partyType ?? '');
      setPickupCpInn(tpl.inn ?? '');
      setPickupCpKpp(tpl.kpp ?? '');
      setPickupCpOgrn(tpl.ogrn ?? '');
    } else {
      setDropoffCounterpartyId(tplId);
      if (!tpl) return;
      setDropoffCounterpartyName(tpl.name);
      if (tpl.country) setDropoffCountry(tpl.country);
      if (tpl.address) setDropoffAddress(tpl.address);
      if (tpl.contactName) setDropoffContact(tpl.contactName);
      if (tpl.contactPhone) setDropoffPhone(tpl.contactPhone);
      setDropoffCpType(tpl.partyType ?? '');
      setDropoffCpInn(tpl.inn ?? '');
      setDropoffCpKpp(tpl.kpp ?? '');
      setDropoffCpOgrn(tpl.ogrn ?? '');
    }
  };

  /** Подставить плательщика из «Моих контрагентов». */
  const applyPayerCounterparty = (tplId: string | null) => {
    setPayerCounterpartyId(tplId);
    const tpl = tplId ? counterparties.find((c) => c.id === tplId) : null;
    if (!tpl) return;
    setPayerName(tpl.name);
    setPayerType(tpl.partyType ?? '');
    setPayerInn(tpl.inn ?? '');
    setPayerKpp(tpl.kpp ?? '');
    setPayerOgrn(tpl.ogrn ?? '');
    setPayerAddress([tpl.country, tpl.address].filter(Boolean).join(', '));
  };

  /** «Проверка»: обязательные поля по текущему состоянию формы (для подсветки). */
  const computeFormErrors = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!agreementId) e.agreementId = 'Выберите договор ТЭО';
    if (!cargoName.trim()) e.cargoName = 'Укажите наименование груза';
    if (cargoWeightKg === '' || Number(cargoWeightKg) <= 0)
      e.cargoWeightKg = 'Укажите вес брутто';
    if (!pickupAddress.trim()) e.pickupAddress = 'Укажите адрес погрузки';
    if (!dropoffAddress.trim()) e.dropoffAddress = 'Укажите адрес выгрузки';
    if (cargoIsDangerous && !cargoAdrClass.trim()) e.cargoAdrClass = 'Укажите класс ДОПОГ';
    if (deliveryTerms === 'CIF') {
      if (!containerNumber.trim()) e.containerNumber = 'Укажите номер контейнера';
      if (!billOfLadingNumber.trim()) e.billOfLadingNumber = 'Укажите номер коносамента';
    }
    return e;
  };

  const onCheck = async () => {
    const e = computeFormErrors();
    // Проверяем, что выбранная связь ГНГ↔ЕТСНГ существует в переходнике.
    if (cargoGng.trim() && cargoEtSng.trim()) {
      try {
        const cands = await referenceDataApi.link('gng', cargoGng.trim());
        if (!cands.some((c) => c.code === cargoEtSng.trim())) {
          e.cargoEtSng = 'Связь ГНГ↔ЕТСНГ не найдена в переходнике';
        }
      } catch {
        /* сеть недоступна — пропускаем проверку связи */
      }
    }
    setFormErrors(e);
    setCheckOk(Object.keys(e).length === 0);
  };

  /**
   * Собирает DTO по текущему состоянию формы. Вынесено из сохранения, чтобы тем
   * же слепком отслеживать несохранённые изменения (см. isDirty).
   */
  const buildDto = (): OrderCreateInput => {
    // При правке шлём пустую строку (очистка сохранится); при создании — undefined.
    const emptyable = (v: string) => (isEdit ? v : v || undefined);

    const routePoints: RoutePointInput[] = [
      {
        kind: 'PICKUP',
        sequence: 1,
        country: pickupCountry || undefined,
        address: pickupAddress,
        contactName: pickupContact || undefined,
        contactPhone: pickupPhone || undefined,
        counterpartyName: pickupCounterpartyName || undefined,
        counterpartyType: (pickupCpType || undefined) as RoutePointInput['counterpartyType'],
        counterpartyInn: pickupCpInn || undefined,
        counterpartyKpp: pickupCpKpp || undefined,
        counterpartyOgrn: pickupCpOgrn || undefined,
        // Дата pickup'а = желаемая дата начала (если задана).
        date: preferredDateFrom ? new Date(preferredDateFrom).toISOString() : undefined,
      },
      {
        kind: 'DROPOFF',
        sequence: 2,
        country: dropoffCountry || undefined,
        address: dropoffAddress,
        contactName: dropoffContact || undefined,
        contactPhone: dropoffPhone || undefined,
        counterpartyName: dropoffCounterpartyName || undefined,
        counterpartyType: (dropoffCpType || undefined) as RoutePointInput['counterpartyType'],
        counterpartyInn: dropoffCpInn || undefined,
        counterpartyKpp: dropoffCpKpp || undefined,
        counterpartyOgrn: dropoffCpOgrn || undefined,
        // Дата dropoff'а = желаемая дата окончания (если задана).
        date: preferredDateTo ? new Date(preferredDateTo).toISOString() : undefined,
      },
    ];

    // Виды транспорта: enum выводим из выбранных кодов ФТС (для ФНС/печати/котировок).
    const transportModes = deriveTransportModes(transportTypeCodes);
    // Производный «основной» вид: единственный или MULTIMODAL.
    const primaryMode = (
      transportModes.length === 1
        ? transportModes[0]
        : transportModes.length > 1
          ? 'MULTIMODAL'
          : 'AUTO'
    ) as OrderCreateInput['transportMode'];

    // Разный груз по контейнерам? Тогда cargoGroups — источник, а основные
    // поля груза — агрегат (для списков/единичных потребителей).
    const multiCargo = showCargoGroups && !sameCargoForAll;
    const num = (v: number | string) => (v === '' ? undefined : Number(v));
    const cargoGroupsDto = multiCargo
      ? cargoGroups.map((g) => ({
          unitIds: g.unitIds,
          cargoName: g.cargoName,
          cargoWeightKg: num(g.cargoWeightKg),
          cargoWeightNetKg: num(g.cargoWeightNetKg),
          cargoVolumeM3: num(g.cargoVolumeM3),
          cargoUnits: num(g.cargoUnits),
          cargoPackaging: g.cargoPackaging || undefined,
          cargoTnVed: g.cargoTnVed || undefined,
          cargoMarking: g.cargoMarking || undefined,
          cargoIsDangerous: g.cargoIsDangerous,
          cargoAdrClass: g.cargoAdrClass || undefined,
        }))
      : undefined;
    const aggCargoName = multiCargo
      ? cargoGroups.map((g) => g.cargoName).filter(Boolean).join(' / ') || 'Сборный груз'
      : cargoName;
    const aggCargoWeightKg = multiCargo
      ? cargoGroups.reduce((s, g) => s + (Number(g.cargoWeightKg) || 0), 0)
      : Number(cargoWeightKg);

    const dto: OrderCreateInput = {
      transportMode: primaryMode,
      transportModes: transportModes as OrderCreateInput['transportModes'],
      transportTypeCodes,
      // Направление и тип отправки — производные: из стран маршрута и из состава
      // единиц транспорта соответственно.
      direction: (derivedDirection ?? 'DOMESTIC') as OrderCreateInput['direction'],
      shipmentType: shipmentType || undefined,
      agreementId: agreementId || undefined,
      sameCargoForAll: !multiCargo,
      cargoGroups: cargoGroupsDto,
      cargoName: aggCargoName,
      cargoCategory: cargoCategory || undefined,
      cargoIsDangerous,
      cargoAdrClass: cargoIsDangerous ? cargoAdrClass : undefined,
      cargoUnNumber: cargoIsDangerous && cargoUnNumber ? cargoUnNumber : undefined,
      cargoTnVed: emptyable(cargoTnVed),
      cargoEtSng: emptyable(cargoEtSng),
      cargoGng: emptyable(cargoGng),
      cargoUnits: cargoUnits === '' ? undefined : Number(cargoUnits),
      cargoWeightKg: aggCargoWeightKg,
      cargoWeightNetKg: cargoWeightNetKg === '' ? undefined : Number(cargoWeightNetKg),
      cargoVolumeM3: cargoVolumeM3 === '' ? undefined : Number(cargoVolumeM3),
      cargoValue: computedValueGross ?? undefined,
      cargoValueNet: cargoValueNet === '' ? undefined : Number(cargoValueNet),
      cargoVatRate: cargoVatRate === '' ? undefined : Number(cargoVatRate),
      cargoValueCurrency: cargoValueCurrency as OrderCreateInput['cargoValueCurrency'],
      cargoPackaging: emptyable(cargoPackaging),
      cargoPackagingCode: emptyable(cargoPackagingCode),
      cargoOriginCountry: emptyable(cargoOriginCountry),
      cargoOriginCountryCode: emptyable(cargoOriginCountryCode),
      cargoOkpd2: emptyable(cargoOkpd2),
      cargoMarking: emptyable(cargoMarking),
      cargoTareWeightKg: cargoTareWeightKg === '' ? undefined : Number(cargoTareWeightKg),
      packageLengthM: packageLengthM === '' ? undefined : Number(packageLengthM),
      packageWidthM: packageWidthM === '' ? undefined : Number(packageWidthM),
      packageHeightM: packageHeightM === '' ? undefined : Number(packageHeightM),
      notifyOnArrival: notifyParties.length > 0,
      notifyParties: notifyParties as OrderCreateInput['notifyParties'],
      expeditorTakesPossession,
      specialMarks: specialMarks || undefined,
      tempMinC: tempMinC === '' ? undefined : Number(tempMinC),
      tempMaxC: tempMaxC === '' ? undefined : Number(tempMaxC),
      transportUnits: units.map((u) => ({
        id: u.id,
        kind: u.kind,
        type: u.type,
        number: u.number.trim() || undefined,
        ownership: u.ownership || undefined,
      })),
      preferredDateFrom: preferredDateFrom
        ? new Date(preferredDateFrom).toISOString()
        : undefined,
      preferredDateTo: preferredDateTo
        ? new Date(preferredDateTo).toISOString()
        : undefined,
      deliveryTerms: (deliveryTerms || undefined) as OrderCreateInput['deliveryTerms'],
      containerNumber:
        deliveryTerms === 'CIF' && containerNumber ? containerNumber : undefined,
      billOfLadingNumber:
        deliveryTerms === 'CIF' && billOfLadingNumber ? billOfLadingNumber : undefined,
      comment: comment || undefined,
      routePoints,
      payerIsClient: payerIsClient,
      // Реквизиты плательщика шлём, только когда платит третье лицо; при
      // возврате к «плательщик — клиент» очищаем (emptyable при правке).
      payerName: payerIsClient ? emptyable('') : payerName || undefined,
      payerType: (payerIsClient ? undefined : payerType || undefined) as
        OrderCreateInput['payerType'],
      payerInn: payerIsClient ? emptyable('') : payerInn || undefined,
      payerKpp: payerIsClient ? emptyable('') : payerKpp || undefined,
      payerOgrn: payerIsClient ? emptyable('') : payerOgrn || undefined,
      payerAddress: payerIsClient ? emptyable('') : payerAddress || undefined,
      routeDetailed: routeDetailed && routeSegments.length > 0,
      // Плечи шлём всегда, когда уточнение включено: пустой массив = «очистить».
      routeSegments: routeDetailed ? routeSegments : [],
      extraServices: extraServices.map((code) => ({
        code,
        name: serviceCatalog.find((s) => s.code === code)?.name,
      })),
    };

    return dto;
  };

  /**
   * Сохранение черновика. Форма НЕ закрывается: «Сохранить» оставляет
   * пользователя в форме и перечитывает поручение с сервера, закрытие —
   * отдельной кнопкой (см. onClose).
   */
  const save = async (): Promise<OrderDetail | null> => {
    setError(null);
    const dto = buildDto();

    // Черновик сохраняем КАК ЕСТЬ — без проверки полноты. Полнота проверяется
    // позже (подача на котировку; «Проверка» для XML — Батч 3).
    setSubmitting(true);
    try {
      let saved: OrderDetail;
      if (isEdit && id) {
        saved = isManager
          ? await managerOrdersApi.update(id, dto)
          : await ordersApi.update(id, dto);
      } else if (isManager) {
        saved = await managerOrdersApi.createForClient(routeClientId ?? '', dto);
      } else {
        saved = await ordersApi.create(dto);
      }

      // Вложения и сохранение в личные справочники — только клиентский режим
      // (менеджеру личные эндпоинты клиента недоступны).
      if (!isManager) {
        for (const file of pendingFiles) {
          await ordersApi.uploadAttachment(saved.id, file);
        }
        for (const file of pendingMsdsRu) {
          await ordersApi.uploadAttachment(saved.id, file, 'MSDS_RU');
        }
        for (const file of pendingMsdsEn) {
          await ordersApi.uploadAttachment(saved.id, file, 'MSDS_EN');
        }
      }

      // Сохраняем в справочники, если попросили
      await Promise.all([
        savePickupAddressToBook && pickupAddress && !pickupAddressId
          ? addressBookApi.create({
              label: pickupCounterpartyName || pickupAddress.slice(0, 60),
              country: pickupCountry || undefined,
              address: pickupAddress,
              contactName: pickupContact || undefined,
              contactPhone: pickupPhone || undefined,
            })
          : Promise.resolve(),
        saveDropoffAddressToBook && dropoffAddress && !dropoffAddressId
          ? addressBookApi.create({
              label: dropoffCounterpartyName || dropoffAddress.slice(0, 60),
              country: dropoffCountry || undefined,
              address: dropoffAddress,
              contactName: dropoffContact || undefined,
              contactPhone: dropoffPhone || undefined,
            })
          : Promise.resolve(),
        saveCargoToBook && cargoName && !cargoTemplateId
          ? (() => {
              // В шаблон сохраняем ЕДИНИЧНЫЕ значения веса/объёма.
              // Если задано количество — делим общие на quantity.
              const qty = Number(cargoUnits);
              const useQty = Number.isFinite(qty) && qty > 0;
              const totalW = Number(cargoWeightKg);
              const totalV = Number(cargoVolumeM3);
              return cargoTemplatesApi.create({
                label: cargoName.slice(0, 60),
                name: cargoName,
                // category — скрыта, не передаём.
                packaging: cargoPackaging || undefined,
                defaultWeightKg:
                  useQty && Number.isFinite(totalW) && totalW > 0
                    ? Math.round((totalW / qty) * 100) / 100
                    : Number.isFinite(totalW) && totalW > 0
                      ? totalW
                      : undefined,
                defaultVolumeM3:
                  useQty && Number.isFinite(totalV) && totalV > 0
                    ? Math.round((totalV / qty) * 100) / 100
                    : Number.isFinite(totalV) && totalV > 0
                      ? totalV
                      : undefined,
                tnVedCode: cargoTnVed || undefined,
                gngCode: cargoGng || undefined,
                etSngCode: cargoEtSng || undefined,
                isDangerous: cargoIsDangerous,
                adrClass: cargoIsDangerous ? cargoAdrClass : undefined,
              });
            })()
          : Promise.resolve(),
        savePickupCounterpartyToBook && pickupCounterpartyName && !pickupCounterpartyId
          ? counterpartiesApi.create({
              label: pickupCounterpartyName.slice(0, 60),
              name: pickupCounterpartyName,
              partyType: (pickupCpType || undefined) as CounterpartyCreateInput['partyType'],
              inn: pickupCpInn || undefined,
              kpp: pickupCpKpp || undefined,
              ogrn: pickupCpOgrn || undefined,
              country: pickupCountry || undefined,
              address: pickupAddress || undefined,
              contactName: pickupContact || undefined,
              contactPhone: pickupPhone || undefined,
            })
          : Promise.resolve(),
        saveDropoffCounterpartyToBook && dropoffCounterpartyName && !dropoffCounterpartyId
          ? counterpartiesApi.create({
              label: dropoffCounterpartyName.slice(0, 60),
              name: dropoffCounterpartyName,
              partyType: (dropoffCpType || undefined) as CounterpartyCreateInput['partyType'],
              inn: dropoffCpInn || undefined,
              kpp: dropoffCpKpp || undefined,
              ogrn: dropoffCpOgrn || undefined,
              country: dropoffCountry || undefined,
              address: dropoffAddress || undefined,
              contactName: dropoffContact || undefined,
              contactPhone: dropoffPhone || undefined,
            })
          : Promise.resolve(),
      ]);

      // Вложения уже загружены на сервер — очищаем очередь, иначе повторное
      // сохранение зальёт их второй раз.
      setPendingFiles([]);
      setPendingMsdsRu([]);
      setPendingMsdsEn([]);

      if (!isEdit) {
        // Новое поручение сохранено — переходим в режим правки того же черновика
        // (иначе следующее «Сохранить» создало бы дубль).
        nav(
          isManager ? `/manager/orders/${saved.id}/edit` : `/cabinet/orders/${saved.id}/edit`,
          { replace: true },
        );
      } else {
        // Перечитываем поручение: показываем то, что реально сохранилось.
        setReloadKey((k) => k + 1);
      }
      setSavedAt(new Date());
      return saved;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
      return null;
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await save();
  };

  /** Есть ли несохранённые изменения (сравнение со слепком последней загрузки). */
  const isDirty = (): boolean =>
    JSON.stringify(buildDto()) !== savedSnapshot.current ||
    pendingFiles.length + pendingMsdsRu.length + pendingMsdsEn.length > 0;

  /** Куда возвращаться из формы. */
  const backTarget = isManager
    ? isEdit && id
      ? `/manager/orders/${id}`
      : routeClientId
        ? `/manager/clients/${routeClientId}`
        : '/manager/orders'
    : isEdit && id
      ? `/cabinet/orders/${id}`
      : '/cabinet/orders';

  const onClose = () => {
    if (isDirty()) setCloseAsk(true);
    else nav(backTarget);
  };

  if (loading) return <Text c="dimmed">Загрузка…</Text>;

  return (
    <Stack>
      <Title order={2}>{isEdit ? 'Редактирование поручения' : 'Новое поручение'}</Title>

      {isManager && (
        <Alert color="grape" title="Поручение от имени клиента">
          Вы оформляете поручение за клиента
          {managerClientName ? `: ${managerClientName}` : ''}. Личные справочники
          клиента (адреса, грузы, контрагенты) недоступны — заполните поля вручную.
        </Alert>
      )}

      {error && (
        <Alert color="red" title="Ошибка">
          {error}
        </Alert>
      )}

      <form onSubmit={onSubmit}>
        <Stack>
          {/* === Шапка: клиент и экспедитор (read-only, из базы) === */}
          <Group grow align="flex-start">
            <TextInput
              label="Клиент"
              description={
                clientInn ? `ИНН ${clientInn}${clientKpp ? `, КПП ${clientKpp}` : ''}` : undefined
              }
              value={clientDisplayName ?? '—'}
              readOnly
              variant="filled"
            />
            <TextInput
              label="Экспедитор"
              description={
                expeditorInn
                  ? `ИНН ${expeditorInn}${expeditorKpp ? `, КПП ${expeditorKpp}` : ''}`
                  : undefined
              }
              value={expeditorName ?? '—'}
              readOnly
              variant="filled"
            />
          </Group>
          <Text size="xs" c="dimmed">
            Клиент и экспедитор подставляются автоматически. Реквизиты сторон по ИНН
            для электронного поручения будут добавлены позже.
          </Text>

          {/* === Плательщик: по умолчанию клиент, иначе — третье лицо === */}
          <Section legend="Плательщик">
            <Stack gap="xs">
              <Checkbox
                label="Плательщик — клиент"
                description="Снимите, если по поручению платит третье лицо"
                checked={payerIsClient}
                onChange={(e) => setPayerIsClient(e.currentTarget.checked)}
              />
              {payerIsClient ? (
                <TextInput
                  label="Плательщик"
                  value={clientDisplayName ?? '—'}
                  readOnly
                  variant="filled"
                />
              ) : (
                <>
                  <CompanyLookup
                    onSelect={(s) =>
                      applyCompanySuggestion(s, {
                        setName: setPayerName,
                        setAddress: setPayerAddress,
                        setType: setPayerType,
                        setInn: setPayerInn,
                        setKpp: setPayerKpp,
                        setOgrn: setPayerOgrn,
                      })
                    }
                  />
                  <TextInput
                    label={
                      <LabelWithAction
                        label="Наименование плательщика"
                        actionLabel="Из «Мои контрагенты»"
                        actionIcon={<IconUsers size={12} />}
                        onAction={() => setPayerPickerOpen(true)}
                        disabled={counterparties.length === 0}
                      />
                    }
                    value={payerName}
                    onChange={(e) => setPayerName(e.currentTarget.value)}
                  />
                  <PartyRequisitesFields
                    type={payerType}
                    setType={setPayerType}
                    inn={payerInn}
                    setInn={setPayerInn}
                    kpp={payerKpp}
                    setKpp={setPayerKpp}
                    ogrn={payerOgrn}
                    setOgrn={setPayerOgrn}
                  />
                  <TextInput
                    label="Адрес плательщика"
                    value={payerAddress}
                    onChange={(e) => setPayerAddress(e.currentTarget.value)}
                  />
                </>
              )}
            </Stack>
          </Section>

          <Select
            label={
              <LabelWithHint
                label="Договор ТЭО (основание поручения)"
                hint="Поручение выдаётся на основании действующего договора транспортной экспедиции"
              />
            }
            placeholder={agreements.length ? 'Выберите договор' : 'Нет действующих договоров'}
            withAsterisk
            clearable
            value={agreementId || null}
            onChange={(v) => setAgreementId(v ?? '')}
            data={agreements.map((a) => ({
              value: a.id,
              label: `№ ${a.number} от ${a.signedDate.slice(0, 10)}`,
            }))}
            disabled={agreements.length === 0}
            error={formErrors.agreementId}
          />
          {agreements.length === 0 && (
            <Text size="xs" c="orange">
              Нет действующих договоров ТЭО. Обратитесь к менеджеру — без договора
              поручение нельзя подать на котировку и сформировать документы.
            </Text>
          )}

          {/* === Стороны: грузоотправитель, грузополучатель === */}
          <Section legend="Стороны поручения">
            <Stack gap="md">
              {/* --- Грузоотправитель (откуда) --- */}
              <Stack gap="xs">
                <Title order={5}>Грузоотправитель (откуда)</Title>
                <CompanyLookup
                  onSelect={(s) =>
                    applyCompanySuggestion(s, {
                      setName: setPickupCounterpartyName,
                      setCountry: setPickupCountry,
                      setAddress: setPickupAddress,
                      setType: setPickupCpType,
                      setInn: setPickupCpInn,
                      setKpp: setPickupCpKpp,
                      setOgrn: setPickupCpOgrn,
                    })
                  }
                />
                <TextInput
                  label={
                    <LabelWithAction
                      label="Наименование отправителя"
                      actionLabel="Из «Мои контрагенты»"
                      actionIcon={<IconUsers size={12} />}
                      onAction={() => setCounterpartyPicker('pickup')}
                      disabled={counterparties.length === 0}
                    />
                  }
                  value={pickupCounterpartyName}
                  onChange={(e) => setPickupCounterpartyName(e.currentTarget.value)}
                />
                <PartyRequisitesFields
                  type={pickupCpType}
                  setType={setPickupCpType}
                  inn={pickupCpInn}
                  setInn={setPickupCpInn}
                  kpp={pickupCpKpp}
                  setKpp={setPickupCpKpp}
                  ogrn={pickupCpOgrn}
                  setOgrn={setPickupCpOgrn}
                />
                <Group grow align="flex-start">
                  <TextInput
                    label={<LabelWithHint label="Страна" hint="Страна отправления" />}
                    placeholder="Россия"
                    value={pickupCountry}
                    onChange={(e) => setPickupCountry(e.currentTarget.value)}
                    style={{ flex: '0 0 30%' }}
                  />
                  <TextInput
                    label={
                      <LabelWithAction
                        label="Адрес погрузки"
                        required
                        actionLabel="Из «Мои адреса»"
                        actionIcon={<IconAddressBook size={12} />}
                        onAction={() => setAddressPicker('pickup')}
                        disabled={addresses.length === 0}
                        hint="Место готовности груза (без страны)"
                      />
                    }
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.currentTarget.value)}
                    style={{ flex: '1 1 70%' }}
                    error={formErrors.pickupAddress}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Контактное лицо"
                    value={pickupContact}
                    onChange={(e) => setPickupContact(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Телефон"
                    value={pickupPhone}
                    onChange={(e) => setPickupPhone(e.currentTarget.value)}
                  />
                </Group>
                <Group>
                  {!pickupAddressId && pickupAddress && (
                    <Switch
                      label="Сохранить адрес в «Мои адреса»"
                      checked={savePickupAddressToBook}
                      onChange={(e) => setSavePickupAddressToBook(e.currentTarget.checked)}
                    />
                  )}
                  {!pickupCounterpartyId && pickupCounterpartyName && (
                    <Switch
                      label="Сохранить в «Мои контрагенты»"
                      checked={savePickupCounterpartyToBook}
                      onChange={(e) => setSavePickupCounterpartyToBook(e.currentTarget.checked)}
                    />
                  )}
                </Group>
              </Stack>

              <Divider />

              {/* --- Грузополучатель (куда) --- */}
              <Stack gap="xs">
                <Title order={5}>Грузополучатель (куда)</Title>
                <CompanyLookup
                  onSelect={(s) =>
                    applyCompanySuggestion(s, {
                      setName: setDropoffCounterpartyName,
                      setCountry: setDropoffCountry,
                      setAddress: setDropoffAddress,
                      setType: setDropoffCpType,
                      setInn: setDropoffCpInn,
                      setKpp: setDropoffCpKpp,
                      setOgrn: setDropoffCpOgrn,
                    })
                  }
                />
                <TextInput
                  label={
                    <LabelWithAction
                      label="Наименование получателя"
                      actionLabel="Из «Мои контрагенты»"
                      actionIcon={<IconUsers size={12} />}
                      onAction={() => setCounterpartyPicker('dropoff')}
                      disabled={counterparties.length === 0}
                    />
                  }
                  value={dropoffCounterpartyName}
                  onChange={(e) => setDropoffCounterpartyName(e.currentTarget.value)}
                />
                <PartyRequisitesFields
                  type={dropoffCpType}
                  setType={setDropoffCpType}
                  inn={dropoffCpInn}
                  setInn={setDropoffCpInn}
                  kpp={dropoffCpKpp}
                  setKpp={setDropoffCpKpp}
                  ogrn={dropoffCpOgrn}
                  setOgrn={setDropoffCpOgrn}
                />
                <Group grow align="flex-start">
                  <TextInput
                    label={<LabelWithHint label="Страна" hint="Страна назначения" />}
                    placeholder="Россия"
                    value={dropoffCountry}
                    onChange={(e) => setDropoffCountry(e.currentTarget.value)}
                    style={{ flex: '0 0 30%' }}
                  />
                  <TextInput
                    label={
                      <LabelWithAction
                        label="Адрес выгрузки"
                        required
                        actionLabel="Из «Мои адреса»"
                        actionIcon={<IconAddressBook size={12} />}
                        onAction={() => setAddressPicker('dropoff')}
                        disabled={addresses.length === 0}
                        hint="Пункт назначения (без страны)"
                      />
                    }
                    value={dropoffAddress}
                    onChange={(e) => setDropoffAddress(e.currentTarget.value)}
                    style={{ flex: '1 1 70%' }}
                    error={formErrors.dropoffAddress}
                  />
                </Group>
                <Group grow>
                  <TextInput
                    label="Контактное лицо"
                    value={dropoffContact}
                    onChange={(e) => setDropoffContact(e.currentTarget.value)}
                  />
                  <TextInput
                    label="Телефон"
                    value={dropoffPhone}
                    onChange={(e) => setDropoffPhone(e.currentTarget.value)}
                  />
                </Group>
                <Group>
                  {!dropoffAddressId && dropoffAddress && (
                    <Switch
                      label="Сохранить адрес в «Мои адреса»"
                      checked={saveDropoffAddressToBook}
                      onChange={(e) => setSaveDropoffAddressToBook(e.currentTarget.checked)}
                    />
                  )}
                  {!dropoffCounterpartyId && dropoffCounterpartyName && (
                    <Switch
                      label="Сохранить в «Мои контрагенты»"
                      checked={saveDropoffCounterpartyToBook}
                      onChange={(e) => setSaveDropoffCounterpartyToBook(e.currentTarget.checked)}
                    />
                  )}
                </Group>
              </Stack>

              <Divider />

              <MultiSelect
                label={
                  <LabelWithHint
                    label="Уведомить о прибытии груза в пункт назначения"
                    hint="Кого уведомить. Пусто — уведомление не требуется."
                  />
                }
                placeholder={notifyParties.length === 0 ? 'Не уведомлять' : undefined}
                clearable
                value={notifyParties}
                onChange={setNotifyParties}
                data={[
                  { value: 'CONSIGNEE', label: 'Грузополучатель' },
                  { value: 'SHIPPER', label: 'Грузоотправитель' },
                  { value: 'CLIENT', label: 'Клиент' },
                ]}
              />
            </Stack>
          </Section>

          {/* === Перевозка: направление, единицы, вид транспорта, плечи, сроки === */}
          <Section legend="Перевозка">
            <Stack>
              {/* Направление следует из стран отправления и назначения —
                  показываем его, а не спрашиваем. */}
              <Group grow align="flex-start">
                <TextInput
                  label={
                    <LabelWithHint
                      label="Направление"
                      hint="Определяется автоматически по странам отправления и назначения: РФ → РФ — внутренняя, РФ → заграница — экспортная, заграница → РФ — импортная, между двумя другими странами — транзитная."
                    />
                  }
                  value={
                    derivedDirection
                      ? directionLabel(derivedDirection)
                      : 'укажите страны отправления и назначения в блоке «Стороны поручения»'
                  }
                  readOnly
                  variant="filled"
                />
                <TextInput
                  label={
                    <Group justify="space-between" gap="xs" wrap="nowrap">
                      <span>Тип отправки</span>
                      <Text size="xs" c="dimmed" fw={400}>
                        Складывается из добавленных единиц транспорта
                      </Text>
                    </Group>
                  }
                  value={shipmentType || 'не указан'}
                  readOnly
                  variant="filled"
                />
              </Group>

              {/* Единицы транспорта: добавляются кнопками (контейнеры, вагоны). */}
              <PositionsEditor value={units} onChange={setUnits} />

              <MultiSelect
                label={
                  <LabelWithHint
                    label="Вид транспорта"
                    hint="Классификатор ФТС. Можно выбрать несколько — мультимодальная перевозка. Код нужен для транзитной декларации."
                  />
                }
                placeholder={transportTypeCodes.length ? undefined : 'Выберите вид(ы) транспорта'}
                value={transportTypeCodes}
                onChange={setTransportTypeCodes}
                data={transportOptions}
                searchable
                withAsterisk
              />

              {/* Уточнение маршрута по плечам — опционально; показываем, когда
                  видов транспорта несколько (или плечи уже заданы). */}
              {(selectedModes.length > 1 || routeDetailed) && (
                <>
                  <Divider />
                  <Checkbox
                    label="Уточнить маршрут по плечам"
                    description="Например: авто от отправителя до станции, жд между станциями, авто от станции до получателя. На авто-плечо внутри России экспедитор сможет сформировать ЭТрН."
                    checked={routeDetailed}
                    onChange={(e) => {
                      const on = e.currentTarget.checked;
                      setRouteDetailed(on);
                      // Первое включение — сразу типовая раскладка по видам транспорта.
                      if (on && routeSegments.length === 0) {
                        setRouteSegments(
                          buildDefaultRouteSegments(selectedModes, {
                            pickupName: pickupAddress,
                            pickupCountry,
                            dropoffName: dropoffAddress,
                            dropoffCountry,
                          }),
                        );
                      }
                    }}
                  />
                  {routeDetailed && (
                    <RouteSegmentsEditor
                      value={routeSegments}
                      onChange={setRouteSegments}
                      modes={selectedModes}
                      ends={{
                        pickupName: pickupAddress,
                        pickupCountry,
                        dropoffName: dropoffAddress,
                        dropoffCountry,
                      }}
                    />
                  )}
                </>
              )}

              <Group grow>
                <DateField
                  label="Готовность груза к отправке: с"
                  value={preferredDateFrom}
                  onChange={setPreferredDateFrom}
                />
                <DateField
                  label="Доставка груза: по"
                  value={preferredDateTo}
                  onChange={setPreferredDateTo}
                />
              </Group>
            </Stack>
          </Section>

          {/* === Груз === */}
          <Section legend="Груз">
            <Stack>
              {/* Отметки о грузе в шапке блока: «один груз на все единицы» и
                  приём во владение экспедитором (формат ФНС: СвГруз/@ОтмПрием). */}
              <Group align="flex-start" gap="xl">
                {showCargoGroups && (
                  <Checkbox
                    label={`Во всех ${unitWordPlural} один груз`}
                    description={`Снимите, чтобы указать разный груз по ${unitWordPlural}`}
                    checked={sameCargoForAll}
                    onChange={(e) => toggleSameCargo(e.currentTarget.checked)}
                  />
                )}
                <Checkbox
                  label="Принять груз во владение экспедитором"
                  checked={expeditorTakesPossession}
                  onChange={(e) => setExpeditorTakesPossession(e.currentTarget.checked)}
                />
              </Group>
              {!sameCargoForAll && showCargoGroups ? (
                <CargoGroupEditor
                  units={units}
                  value={cargoGroups}
                  onChange={setCargoGroups}
                  kindLabelPlural={unitWordPlural}
                />
              ) : (
                <>
              <TextInput
                label={
                  <LabelWithAction
                    label="Наименование груза"
                    required
                    actionLabel={cargoTemplateId ? 'Сменить шаблон' : 'Из «Мои грузы»'}
                    actionIcon={<IconBoxSeam size={12} />}
                    onAction={() => setCargoTemplatePickerOpen(true)}
                    disabled={cargos.length === 0}
                  />
                }
                description={
                  cargoTemplateId ? (
                    <Text
                      component="a"
                      size="xs"
                      c="dimmed"
                      style={{ cursor: 'pointer' }}
                      onClick={() => applyCargoTemplate(null)}
                    >
                      Описание подтянуто из шаблона — нажмите чтобы отвязать
                    </Text>
                  ) : (
                    'Для экспедитора и документов'
                  )
                }
                value={cargoName}
                onChange={(e) => setCargoName(e.currentTarget.value)}
                error={formErrors.cargoName}
              />
              <CodeSelect
                fetchOptions={(q) => referenceDataApi.classifierSearch('country', q)}
                fetchByCode={(c) => referenceDataApi.classifierByCode('country', c)}
                label={
                  <LabelWithHint
                    label="Страна происхождения груза"
                    hint="Классификатор стран ФТС (alpha-2). Выбор сохранит код — нужен для транзитной декларации."
                  />
                }
                value={cargoOriginCountryCode}
                onChange={setCargoOriginCountryCode}
              />
              {/* Товарный код */}
              <Group grow>
                <CodeSelect
                  fetchOptions={(q) => filterOrSearch(gngConstraint, () => referenceDataApi.search('gng', q), q)}
                  fetchByCode={(c) => referenceDataApi.byCode('gng', c)}
                  label={
                    <LabelWithHint
                      label="ГНГ"
                      hint="Код ГНГ (8 знаков) — номенклатура ОСЖД/СМГС. По переходнику подтянется ЕТСНГ (или предложит варианты в поле ЕТ СНГ)."
                    />
                  }
                  value={cargoGng}
                  error={formErrors.cargoGng}
                  onChange={(v) => {
                    setCargoGng(v);
                    setFormErrors((e) => ({ ...e, cargoGng: '', cargoEtSng: '' }));
                    if (!v) setEtsngConstraint(null);
                  }}
                />
                <CodeSelect
                  fetchOptions={(q) => filterOrSearch(etsngConstraint, () => referenceDataApi.search('etsng', q), q)}
                  fetchByCode={(c) => referenceDataApi.byCode('etsng', c)}
                  label={
                    <LabelWithHint
                      label="ЕТ СНГ"
                      hint="Код ЕТСНГ (6 знаков) — номенклатура РЖД для тарифа. По переходнику подтянется ГНГ (или предложит варианты в поле ГНГ); при опасном грузе включится отметка."
                    />
                  }
                  value={cargoEtSng}
                  error={formErrors.cargoEtSng}
                  onChange={(v) => {
                    setCargoEtSng(v);
                    setFormErrors((e) => ({ ...e, cargoGng: '', cargoEtSng: '' }));
                    if (!v) {
                      setGngConstraint(null);
                      setEtsngInfo(null);
                    }
                  }}
                />
                <TextInput
                  label="ТН ВЭД"
                  value={cargoTnVed}
                  onChange={(e) => setCargoTnVed(e.currentTarget.value)}
                />
                <TextInput
                  label="ОКПД 2"
                  value={cargoOkpd2}
                  onChange={(e) => setCargoOkpd2(e.currentTarget.value)}
                />
              </Group>
              {etsngInfo && (etsngInfo.dangerous || etsngInfo.perishable) && (
                <Alert color={etsngInfo.dangerous ? 'orange' : 'blue'} variant="light" py={6}>
                  По коду ЕТСНГ {cargoEtSng.trim()} груз помечен как
                  {etsngInfo.dangerous ? ' опасный' : ''}
                  {etsngInfo.dangerous && etsngInfo.perishable ? ' и' : ''}
                  {etsngInfo.perishable ? ' скоропортящийся' : ''}.
                  {etsngInfo.dangerous
                    ? ' Отметка «Опасный груз» установлена автоматически — укажите класс опасности и № ООН.'
                    : ''}
                </Alert>
              )}
              {etsngConstraint && etsngConstraint.length > 1 && !cargoEtSng && (
                <Text size="xs" c="blue">
                  По переходнику: выберите ЕТ СНГ из {etsngConstraint.length} вариантов в поле «ЕТ СНГ».
                </Text>
              )}
              {gngConstraint && gngConstraint.length > 1 && !cargoGng && (
                <Text size="xs" c="blue">
                  По переходнику: выберите ГНГ из {gngConstraint.length} вариантов в поле «ГНГ».
                </Text>
              )}
              <Textarea
                label={
                  <LabelWithHint
                    label="Маркировка"
                    hint="Маркировка груза в соответствии с законодательством РФ"
                  />
                }
                autosize
                minRows={1}
                value={cargoMarking}
                onChange={(e) => setCargoMarking(e.currentTarget.value)}
              />
              {/* Количество мест и упаковка */}
              <Group grow>
                <NumberInput
                  label="Количество мест"
                  description={
                    unitWeightKg || unitVolumeM3
                      ? 'При вводе пересчитает вес и объём из шаблона'
                      : undefined
                  }
                  value={cargoUnits}
                  onChange={onUnitsChange}
                  min={0}
                  step={1}
                  allowDecimal={false}
                />
                <CodeSelect
                  fetchOptions={(q) => referenceDataApi.classifierSearch('packaging', q)}
                  fetchByCode={(c) => referenceDataApi.classifierByCode('packaging', c)}
                  label={
                    <LabelWithHint
                      label="Вид упаковки"
                      hint="Классификатор видов упаковки ФТС. Выбор сохранит код — нужен для транзитной декларации."
                    />
                  }
                  value={cargoPackagingCode}
                  onChange={setCargoPackagingCode}
                />
              </Group>
              {/* Вес */}
              <Group grow>
                <NumberInput
                  label="Вес брутто (с упаковкой), кг"
                  withAsterisk
                  value={cargoWeightKg}
                  onChange={handleGrossChange}
                  min={0}
                  decimalScale={2}
                  step={0.01}
                  error={formErrors.cargoWeightKg}
                />
                <NumberInput
                  label="Вес нетто (без упаковки), кг"
                  value={cargoWeightNetKg}
                  onChange={handleNetChange}
                  min={0}
                  decimalScale={2}
                  step={0.01}
                />
                <NumberInput
                  label={
                    <LabelWithHint
                      label="Вес упаковки, кг"
                      hint="Заполняется автоматически как брутто − нетто; можно изменить вручную"
                    />
                  }
                  value={cargoTareWeightKg}
                  onChange={handleTareChange}
                  min={0}
                  decimalScale={2}
                  step={0.01}
                />
                <NumberInput
                  label="Объём, м³"
                  value={cargoVolumeM3}
                  onChange={setCargoVolumeM3}
                  min={0}
                  decimalScale={2}
                  step={0.01}
                />
              </Group>
              {/* Размер упаковки (грузового места), метры */}
              <Group grow>
                <NumberInput
                  label="Размер упаковки — длина, м"
                  value={packageLengthM}
                  onChange={setPackageLengthM}
                  min={0}
                  decimalScale={3}
                  step={0.01}
                />
                <NumberInput
                  label="Ширина, м"
                  value={packageWidthM}
                  onChange={setPackageWidthM}
                  min={0}
                  decimalScale={3}
                  step={0.01}
                />
                <NumberInput
                  label="Высота, м"
                  value={packageHeightM}
                  onChange={setPackageHeightM}
                  min={0}
                  decimalScale={3}
                  step={0.01}
                />
              </Group>
              {/* Опасный груз */}
              <Stack gap="xs">
                <Checkbox
                  label="Опасный груз"
                  checked={cargoIsDangerous}
                  onChange={(e) => setCargoIsDangerous(e.currentTarget.checked)}
                />
                {cargoIsDangerous && (
                  <Stack gap="xs" pl="md">
                    <Group grow>
                      <TextInput
                        label="Класс ДОПОГ"
                        withAsterisk
                        value={cargoAdrClass}
                        onChange={(e) => setCargoAdrClass(e.currentTarget.value)}
                        error={formErrors.cargoAdrClass}
                      />
                      <TextInput
                        label={<LabelWithHint label="ООН-номер" hint="4-значный номер по списку UN" />}
                        placeholder="1203"
                        value={cargoUnNumber}
                        onChange={(e) => setCargoUnNumber(e.currentTarget.value)}
                      />
                    </Group>
                    <FileInput
                      label="МСДС на русском"
                      description="Паспорт безопасности (MSDS / SDS). Можно несколько файлов."
                      multiple
                      value={pendingMsdsRu}
                      onChange={setPendingMsdsRu}
                      accept=".pdf,.doc,.docx,.jpg,.png"
                    />
                    <FileInput
                      label="МСДС на английском"
                      description="Material Safety Data Sheet. Можно несколько файлов."
                      multiple
                      value={pendingMsdsEn}
                      onChange={setPendingMsdsEn}
                      accept=".pdf,.doc,.docx,.jpg,.png"
                    />
                  </Stack>
                )}
              </Stack>
                </>
              )}
              {/* Условия поставки */}
              <Group grow>
                <Select
                  label="Условия поставки по контракту"
                  placeholder="Incoterms 2020"
                  clearable
                  searchable
                  value={deliveryTerms || null}
                  onChange={(v) => setDeliveryTerms(v ?? '')}
                  data={[
                    { value: 'EXW', label: 'EXW — Ex Works' },
                    { value: 'FCA', label: 'FCA — Free Carrier' },
                    { value: 'CPT', label: 'CPT — Carriage Paid To' },
                    { value: 'CIP', label: 'CIP — Carriage and Insurance Paid To' },
                    { value: 'DAP', label: 'DAP — Delivered at Place' },
                    { value: 'DPU', label: 'DPU — Delivered at Place Unloaded' },
                    { value: 'DDP', label: 'DDP — Delivered Duty Paid' },
                    { value: 'FAS', label: 'FAS — Free Alongside Ship' },
                    { value: 'FOB', label: 'FOB — Free On Board' },
                    { value: 'CFR', label: 'CFR — Cost and Freight' },
                    { value: 'CIF', label: 'CIF — Cost, Insurance and Freight' },
                  ]}
                />
              </Group>
              {deliveryTerms === 'CIF' && (
                <Group grow>
                  <TextInput
                    label="Номер контейнера"
                    withAsterisk
                    placeholder="MSCU1234567"
                    value={containerNumber}
                    onChange={(e) => setContainerNumber(e.currentTarget.value)}
                    error={formErrors.containerNumber}
                  />
                  <TextInput
                    label="Номер коносамента"
                    withAsterisk
                    placeholder="MSCUAB1234567"
                    value={billOfLadingNumber}
                    onChange={(e) => setBillOfLadingNumber(e.currentTarget.value)}
                    error={formErrors.billOfLadingNumber}
                  />
                </Group>
              )}
              {!cargoTemplateId && cargoName && (
                <Switch
                  label="Сохранить в «Мои грузы»"
                  checked={saveCargoToBook}
                  onChange={(e) => setSaveCargoToBook(e.currentTarget.checked)}
                />
              )}
            </Stack>
          </Section>

          {/* === Стоимость === */}
          <Section legend="Стоимость груза">
            <Stack>
              <Group grow>
                <NumberInput
                  label="Стоимость без налога"
                  value={cargoValueNet}
                  onChange={setCargoValueNet}
                  min={0}
                  decimalScale={2}
                  step={0.01}
                />
                <CodeSelect
                  fetchOptions={(q) => referenceDataApi.classifierSearch('currency', q)}
                  fetchByCode={(c) => referenceDataApi.classifierByCode('currency', c)}
                  label="Валюта"
                  value={cargoValueCurrency}
                  clearable={false}
                  onChange={(v) => setCargoValueCurrency(v || 'RUB')}
                />
                <Select
                  label="Ставка НДС"
                  placeholder="не указана"
                  clearable
                  value={cargoVatRate || null}
                  onChange={(v) => setCargoVatRate(v ?? '')}
                  data={VAT_RATE_OPTIONS}
                />
              </Group>
              <Group grow>
                <NumberInput
                  label="Стоимость налога"
                  value={computedVatAmount ?? ''}
                  onChange={() => undefined}
                  readOnly
                  decimalScale={2}
                />
                <NumberInput
                  label="Стоимость с налогом"
                  value={computedValueGross ?? ''}
                  onChange={() => undefined}
                  readOnly
                  decimalScale={2}
                />
              </Group>
            </Stack>
          </Section>

          {/* === Страхование и доп. услуги === */}
          <Section legend="Страхование и доп. услуги">
            <MultiSelect
              data={serviceCatalog.map((s) => ({ value: s.code, label: s.name }))}
              value={extraServices}
              onChange={setExtraServices}
              placeholder={
                serviceCatalog.length ? 'Выберите услуги' : 'Справочник услуг пуст'
              }
              disabled={serviceCatalog.length === 0}
            />
          </Section>

          {/* === Требуемые документы === */}
          <Section legend="Требуемые документы">
            <Stack>
              {existingAttachments.length > 0 && (
                <Stack gap={4}>
                  {existingAttachments.map((a) => (
                    <Text key={a.id} size="sm">
                      📎 {a.fileName} ({Math.round(a.sizeBytes / 1024)} KB)
                    </Text>
                  ))}
                </Stack>
              )}
              <FileInput
                label="Прикрепить новые файлы (можно несколько)"
                placeholder="Выберите файлы"
                multiple
                value={pendingFiles}
                onChange={(files) => setPendingFiles(files ?? [])}
              />
            </Stack>
          </Section>

          {/* === Особые отметки === */}
          <Section legend="Особые отметки">
            <Stack>
              <Textarea
                label={
                  <LabelWithHint
                    label="Особые отметки"
                    hint="Дополнительная информация о грузе и условиях перевозки"
                  />
                }
                minRows={2}
                autosize
                value={specialMarks}
                onChange={(e) => setSpecialMarks(e.currentTarget.value)}
              />
              <Group grow>
                <NumberInput
                  label="Температура не ниже, °C"
                  value={tempMinC}
                  onChange={setTempMinC}
                  decimalScale={1}
                  step={1}
                />
                <NumberInput
                  label="Температура не выше, °C"
                  value={tempMaxC}
                  onChange={setTempMaxC}
                  decimalScale={1}
                  step={1}
                />
              </Group>
              <Textarea
                label="Комментарий (внутренний)"
                minRows={2}
                value={comment}
                onChange={(e) => setComment(e.currentTarget.value)}
                placeholder="Особые пожелания, требования к перевозке"
              />
            </Stack>
          </Section>

          {Object.keys(formErrors).length > 0 && (
            <Alert color="orange" title="Не заполнены обязательные поля">
              Поля с ошибками отмечены в форме красным — заполните их перед подачей на
              котировку и формированием документов.
            </Alert>
          )}
          {checkOk && (
            <Alert
              color="green"
              title="Проверка пройдена"
              withCloseButton
              onClose={() => setCheckOk(false)}
            >
              Все обязательные поля заполнены.
            </Alert>
          )}
          <Group justify="flex-end">
            <Button type="button" variant="default" onClick={onCheck}>
              Проверка
            </Button>
            <Button type="button" variant="default" onClick={onClose}>
              Закрыть
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? 'Сохранить' : 'Создать поручение'}
            </Button>
          </Group>
          {savedAt && (
            <Text size="xs" c="dimmed" ta="right">
              Сохранено в {savedAt.toLocaleTimeString('ru-RU')} — форма осталась открытой
            </Text>
          )}
        </Stack>
      </form>

      {/* --- Пикер шаблона груза --- */}
      <EntityPicker<CargoTemplateDto>
        opened={cargoTemplatePickerOpen}
        onClose={() => setCargoTemplatePickerOpen(false)}
        title="Выбрать из «Мои грузы»"
        items={cargos.map((c) => ({
          ...c,
          subline: c.name,
          details: c.isDangerous ? `Опасный — класс ДОПОГ ${c.adrClass ?? '?'}` : null,
        }))}
        selectedId={cargoTemplateId}
        onSelect={(c) => {
          applyCargoTemplate(c.id);
          setCargoTemplatePickerOpen(false);
        }}
        emptyText="У вас пока нет шаблонов в «Мои грузы»"
      />

      {/* --- Пикер контрагента (для отправителя или получателя) --- */}
      <EntityPicker<CounterpartyDto>
        opened={counterpartyPicker !== null}
        onClose={() => setCounterpartyPicker(null)}
        title={
          counterpartyPicker === 'pickup'
            ? 'Выбрать отправителя'
            : 'Выбрать получателя'
        }
        items={counterparties.map((c) => ({
          ...c,
          subline: c.inn ? `ИНН ${c.inn}` : c.name,
          details: c.country
            ? `${c.country}${c.address ? `, ${c.address}` : ''}`
            : c.address ?? null,
        }))}
        selectedId={
          counterpartyPicker === 'pickup' ? pickupCounterpartyId : dropoffCounterpartyId
        }
        onSelect={(c) => {
          applyCounterparty(counterpartyPicker === 'pickup' ? 'pickup' : 'dropoff', c.id);
          setCounterpartyPicker(null);
        }}
        emptyText="У вас пока нет контрагентов в «Мои контрагенты»"
      />

      {/* --- Закрытие с несохранёнными изменениями --- */}
      <Modal
        opened={closeAsk}
        onClose={() => setCloseAsk(false)}
        title="Закрыть форму?"
        size="md"
      >
        <Stack>
          <Text size="sm">В форме есть несохранённые изменения. Сохранить их перед закрытием?</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCloseAsk(false)}>
              Отмена
            </Button>
            <Button
              variant="default"
              color="red"
              onClick={() => {
                setCloseAsk(false);
                nav(backTarget);
              }}
            >
              Закрыть без сохранения
            </Button>
            <Button
              loading={submitting}
              onClick={async () => {
                const saved = await save();
                if (!saved) return;
                setCloseAsk(false);
                nav(isManager ? `/manager/orders/${saved.id}` : `/cabinet/orders/${saved.id}`);
              }}
            >
              Сохранить и закрыть
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* --- Пикер плательщика --- */}
      <EntityPicker<CounterpartyDto>
        opened={payerPickerOpen}
        onClose={() => setPayerPickerOpen(false)}
        title="Выбрать плательщика"
        items={counterparties.map((c) => ({
          ...c,
          subline: c.inn ? `ИНН ${c.inn}` : c.name,
          details: c.country ? `${c.country}${c.address ? `, ${c.address}` : ''}` : c.address ?? null,
        }))}
        selectedId={payerCounterpartyId}
        onSelect={(c) => {
          applyPayerCounterparty(c.id);
          setPayerPickerOpen(false);
        }}
        emptyText="У вас пока нет контрагентов в «Мои контрагенты»"
      />

      {/* --- Пикер адреса (для погрузки или выгрузки) --- */}
      <EntityPicker<AddressBookEntryDto>
        opened={addressPicker !== null}
        onClose={() => setAddressPicker(null)}
        title={
          addressPicker === 'pickup'
            ? 'Выбрать адрес погрузки'
            : 'Выбрать адрес выгрузки'
        }
        items={addresses.map((a) => ({
          ...a,
          subline: a.country ? `${a.country}, ${a.address}` : a.address,
          details: a.contactName ?? null,
        }))}
        selectedId={addressPicker === 'pickup' ? pickupAddressId : dropoffAddressId}
        onSelect={(a) => {
          if (addressPicker === 'pickup') {
            applyAddress(
              setPickupAddressId,
              setPickupCountry,
              setPickupAddress,
              setPickupContact,
              setPickupPhone,
              a.id,
            );
          } else {
            applyAddress(
              setDropoffAddressId,
              setDropoffCountry,
              setDropoffAddress,
              setDropoffContact,
              setDropoffPhone,
              a.id,
            );
          }
          setAddressPicker(null);
        }}
        emptyText="У вас пока нет адресов в «Мои адреса»"
      />
    </Stack>
  );
}

/**
 * Раздел формы — стилизованный Fieldset с легендой на рамке (как печатный
 * бланк). Легенда крупная, жирная, оранжевая; рамка тоже оранжевая.
 */
function Section({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <Fieldset
      legend={legend}
      styles={{
        root: { borderColor: 'var(--mantine-color-orange-4)' },
        legend: {
          fontSize: 'var(--mantine-font-size-lg)',
          fontWeight: 700,
          color: 'var(--mantine-color-orange-7)',
        },
      }}
    >
      {children}
    </Fieldset>
  );
}
